import React, { useState, useCallback, useEffect, useRef } from 'react';
import CommonModalLayout from '../Layouts/CommonModalLayout';
import {
    buttonStyles,
    primaryButtonStyles,
    addPostcardModalStyles,
    allowedFormatsTextStyles
} from '../../styles/commonStyles';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useQuery } from 'react-query';
import useFileValidation, { ALLOWED_FORMATS } from '../../hooks/useFileValidation';
import FileDropZone from '../FileUpload/FileDropZone';
import MediaPreview from '../FileUpload/MediaPreview';
import ErrorMessage from '../Common/ErrorMessage';
import WarningMessage from '../Common/WarningMessage';
import { uploadFileToS3, useCreateCatalogUpload } from '../../api/uploads';
import { getCollectionCategoriesGrouped } from '../../api/collections';
import { EnhancedCategoryCombobox } from '../ui/EnhancedCategoryCombobox';

const AddPostcardModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [image, setImage] = useState(null);
    const [publish, setPublish] = useState(true);
    const [step, setStep] = useState(1);
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const queryClient = useQueryClient();
    const fileDropZoneRef = useRef(null);
    const createUpload = useCreateCatalogUpload();

    // Получаем ВСЕ категории для выбора при загрузке (include_all=true)
    const { data: groupedData } = useQuery(
        ['gallery-categories-all', 9],
        () => getCollectionCategoriesGrouped(9, { includeAll: true }),
        {
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
        }
    );
    const [selectedCategory, setSelectedCategory] = useState('none');

    const groupedCategories = groupedData?.groups || [];
    const categoriesWithoutGroup = groupedData?.categories_without_group || [];

    const {
        error,
        warning,
        setError,
        setWarning,
        validateFile
    } = useFileValidation();

    // Сброс состояния при открытии
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setImage(null);
            setPublish(true);
            setIsUploading(false);
            setIsSaving(false);
            setError('');
            setWarning('');
            setSelectedCategory('none');
        }
    }, [isOpen]);

    // Клавиша 'o'/'щ' для открытия выбора файла
    useEffect(() => {
        if (!(isOpen && step === 1)) return;
        const handleKeyDown = (e) => {
            if ((e.key === 'o' || e.key === 'щ') && fileDropZoneRef.current) {
                const input = fileDropZoneRef.current.querySelector('input[type="file"]');
                if (input) input.click();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, step]);

    const handleFileUploadSuccess = (data) => {
        setImage({
            cloud_url: data.cloud_url,
            hash: data.hash,
            upload_id: data.id,
            file_name: data.file_name || data.original_name,
            file_size: data.file_size || data.size
        });
        setStep(2);
        setIsUploading(false);
    };

    const handleFileUpload = async (file) => {
        if (!validateFile(file)) return;

        setIsUploading(true);
        try {
            const data = await uploadFileToS3(file);
            // Сохраняем имя и размер файла из оригинала
            data.file_name = file.name;
            data.file_size = file.size;
            handleFileUploadSuccess(data);
        } catch (error) {
            setIsUploading(false);
            setError(t('addPostcardModal.uploadError', { error: error.message }));
        }
    };

    const handleSave = async () => {
        setError('');

        if (!image || !image.cloud_url) {
            setError(t('addPostcardModal.noImageUrl', 'Нет URL изображения'));
            return;
        }

        setIsSaving(true);
        try {
            await createUpload.mutateAsync({
                cloud_url: image.cloud_url,
                upload_id: image.upload_id || null,
                file_name: image.file_name || null,
                file_size: image.file_size || null,
                category_id: selectedCategory === 'none' ? null : parseInt(selectedCategory),
                publish
            });

            onClose();
        } catch (error) {
            console.error('Error saving upload:', error);
            setError(error.message || t('addPostcardModal.errorMessage', 'Ошибка сохранения'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = useCallback(() => {
        setStep(1);
        setImage(null);
        setPublish(true);
        setIsUploading(false);
        setIsSaving(false);
        setError('');
        setWarning('');
        setSelectedCategory('none');
        onClose();
    }, [onClose]);

    if (!isOpen) return null;

    return (
        <CommonModalLayout
            isOpen={isOpen}
            onClose={handleClose}
            title={`${t('addPostcardModal.title', 'Добавить открытку')} (${step}/2)`}
            closeOnOverlayClick={false}
            customStyles={addPostcardModalStyles}
        >
            <div className="px-0 py-0">
                {/* Шаг 1: Загрузка изображения */}
                {step === 1 && (
                    <div>
                        <FileDropZone
                            onFileSelect={handleFileUpload}
                            allowedFormats={ALLOWED_FORMATS}
                            isUploading={isUploading}
                            ref={fileDropZoneRef}
                        />

                        <div className={allowedFormatsTextStyles}>
                            {t('addPostcardModal.allowedFormats', {
                                formats: ALLOWED_FORMATS.join(', ')
                            })}
                        </div>

                        <ErrorMessage message={error} />
                        <WarningMessage message={warning} />

                        <div className="flex justify-end space-x-2 mt-5">
                            <button onClick={handleClose} className={buttonStyles}>
                                {t('addPostcardModal.cancelButton', 'Отмена')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Шаг 2: Категория + название + сохранение */}
                {step === 2 && (
                    <div>
                        <MediaPreview media={image} />

                        {/* Категория */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('categories.selectCategory', 'Категория')}
                            </label>
                            <EnhancedCategoryCombobox
                                groupedCategories={groupedCategories}
                                categoriesWithoutGroup={categoriesWithoutGroup}
                                value={selectedCategory}
                                onChange={setSelectedCategory}
                                placeholder={t('categories.selectCategory', 'Категория')}
                                filterPlaceholder={t('categories.filterPlaceholder', 'Поиск...')}
                                showCreateOptions={false}
                            />
                        </div>

                        <ErrorMessage message={error} />

                        {/* Публикация */}
                        <label className="flex items-center gap-2 mb-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={publish}
                                onChange={(e) => setPublish(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                Опубликовать в общий каталог
                            </span>
                        </label>

                        <div className="flex justify-between space-x-2 pt-2">
                            <button onClick={() => setStep(1)} className={buttonStyles}>
                                {t('addPostcardModal.backButton', 'Назад')}
                            </button>
                            <div className="flex space-x-2">
                                <button onClick={handleClose} className={buttonStyles}>
                                    {t('addPostcardModal.cancelButton', 'Отмена')}
                                </button>
                                <button
                                    onClick={handleSave}
                                    className={`${primaryButtonStyles} px-4 py-2 disabled:opacity-50`}
                                    disabled={isSaving}
                                >
                                    {isSaving
                                        ? t('addPostcardModal.saving', 'Сохранение...')
                                        : t('addPostcardModal.saveButton', 'Сохранить')
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </CommonModalLayout>
    );
};

export default AddPostcardModal;
